import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getApiUrl, logout as logoutSession, verifySession } from './adminApi';
import {
  FaArrowLeft,
  FaSave, 
  FaImage, 
  FaTimes,
  FaShoppingCart,
  FaChartLine,
  FaComments,
  FaCheck,
  FaBolt,
  FaTwitter,
  FaLightbulb,
  FaDatabase,
  FaBullseye,
  FaRocket,
  FaCode,
  FaFolder,
  FaHome,
  FaTools,
  FaUser,
  FaEnvelope,
  FaPlus,
  FaTrash,
  FaEdit,
  FaProjectDiagram,
  FaStar,
  FaSearch,
  FaSignOutAlt
} from 'react-icons/fa';
import {
  SiReact,
  SiPython,
  SiJavascript,
  SiNodedotjs,
  SiMongodb,
  SiDocker,
  SiExpress,
  SiTypescript,
  SiPostgresql,
  SiSocketdotio,
  SiRedis,
  SiChartdotjs,
  SiDjango,
  SiVuedotjs,
  SiGraphql,
  SiHtml5,
  SiCss3,
  SiGit,
  SiMysql,
  SiFigma,
  SiNextdotjs,
  SiTailwindcss,
  SiBootstrap,
  SiSass,
  SiJest,
  SiWebpack,
  SiFirebase,
  SiAmazonaws,
  SiAngular,
  SiPhp,
  SiOpenjdk,
  SiCsharp,
  SiGo,
  SiRust,
  SiSwift,
  SiKotlin
} from 'react-icons/si';
import { FaKey } from 'react-icons/fa';
import './App.css';

// Fonction pour générer la palette de couleurs
const generateColorPalette = (baseColor) => {
  const hex = baseColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  const lighter = `rgb(${Math.min(255, r + 30)}, ${Math.min(255, g + 30)}, ${Math.min(255, b + 30)})`;
  const darker = `rgb(${Math.max(0, r - 30)}, ${Math.max(0, g - 30)}, ${Math.max(0, b - 30)})`;
  const gradient = `linear-gradient(135deg, ${baseColor} 0%, ${darker} 100%)`;
  
  return { lighter, darker, gradient, r, g, b };
};

// Fonction pour appliquer les variables CSS
const applyThemeAndColor = (isDark, accentColor) => {
  document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  document.documentElement.style.setProperty('--accent-color', accentColor);
  
  const palette = generateColorPalette(accentColor);
  
  document.documentElement.style.setProperty('--accent-gradient', palette.gradient);
  document.documentElement.style.setProperty('--accent-light', palette.lighter);
  document.documentElement.style.setProperty('--accent-dark', palette.darker);
  document.documentElement.style.setProperty('--accent-color-rgb', `${palette.r}, ${palette.g}, ${palette.b}`);
};

// Configuration des technologies disponibles
const availableTechs = {
  // Frontend
  'HTML': { icon: SiHtml5, color: '#E34F26', defaultLevel: 90 },
  'CSS': { icon: SiCss3, color: '#1572B6', defaultLevel: 90 },
  'HTML5': { icon: SiHtml5, color: '#E34F26', defaultLevel: 90 },
  'CSS3': { icon: SiCss3, color: '#1572B6', defaultLevel: 90 },
  'JavaScript': { icon: SiJavascript, color: '#F7DF1E', defaultLevel: 92 },
  'TypeScript': { icon: SiTypescript, color: '#3178C6', defaultLevel: 85 },
  'React': { icon: SiReact, color: '#61DAFB', defaultLevel: 95 },
  'Vue.js': { icon: SiVuedotjs, color: '#4FC08D', defaultLevel: 75 },
  'Angular': { icon: SiAngular, color: '#DD0031', defaultLevel: 70 },
  'Next.js': { icon: SiNextdotjs, color: '#000000', defaultLevel: 80 },
  'Tailwind CSS': { icon: SiTailwindcss, color: '#06B6D4', defaultLevel: 85 },
  'Bootstrap': { icon: SiBootstrap, color: '#7952B3', defaultLevel: 80 },
  'Sass': { icon: SiSass, color: '#CC6699', defaultLevel: 75 },
  
  // Backend
  'Node.js': { icon: SiNodedotjs, color: '#339933', defaultLevel: 88 },
  'Express': { icon: SiExpress, color: '#404040', defaultLevel: 82 },
  'Python': { icon: SiPython, color: '#3776AB', defaultLevel: 90 },
  'Django': { icon: SiDjango, color: '#092E20', defaultLevel: 75 },
  'PHP': { icon: SiPhp, color: '#777BB4', defaultLevel: 75 },
  'Java': { icon: SiOpenjdk, color: '#ED8B00', defaultLevel: 70 },
  'C#': { icon: SiCsharp, color: '#239120', defaultLevel: 70 },
  'Go': { icon: SiGo, color: '#00ADD8', defaultLevel: 65 },
  'Rust': { icon: SiRust, color: '#000000', defaultLevel: 60 },
  
  // Bases de données
  'MongoDB': { icon: SiMongodb, color: '#47A248', defaultLevel: 85 },
  'PostgreSQL': { icon: SiPostgresql, color: '#336791', defaultLevel: 80 },
  'MySQL': { icon: SiMysql, color: '#4479A1', defaultLevel: 85 },
  'Redis': { icon: SiRedis, color: '#DC382D', defaultLevel: 75 },
  
  // Outils et DevOps
  'Docker': { icon: SiDocker, color: '#2496ED', defaultLevel: 85 },
  'Git': { icon: SiGit, color: '#F05032', defaultLevel: 85 },
  'Webpack': { icon: SiWebpack, color: '#8DD6F9', defaultLevel: 70 },
  'Jest': { icon: SiJest, color: '#C21325', defaultLevel: 75 },
  
  // Services et Cloud
  'Firebase': { icon: SiFirebase, color: '#FFCA28', defaultLevel: 75 },
  'AWS': { icon: SiAmazonaws, color: '#FF9900', defaultLevel: 70 },
  
  // Communication et autres
  'Socket.io': { icon: SiSocketdotio, color: '#010101', defaultLevel: 78 },
  'GraphQL': { icon: SiGraphql, color: '#E10098', defaultLevel: 70 },
  'Chart.js': { icon: SiChartdotjs, color: '#FF6384', defaultLevel: 70 },
  'Figma': { icon: SiFigma, color: '#F24E1E', defaultLevel: 80 },
  'JWT': { icon: FaKey, color: '#000000', defaultLevel: 80 },
  
  // Mobile
  'Swift': { icon: SiSwift, color: '#FA7343', defaultLevel: 60 },
  'Kotlin': { icon: SiKotlin, color: '#7F52FF', defaultLevel: 60 }
};

// Mapping des icônes par nom (pour afficher les projets)
const iconMap = {
  'FaShoppingCart': FaShoppingCart,
  'FaChartLine': FaChartLine,
  'FaComments': FaComments,
  'FaCheck': FaCheck,
  'FaBolt': FaBolt,
  'FaTwitter': FaTwitter,
  'FaLightbulb': FaLightbulb,
  'FaDatabase': FaDatabase,
  'FaBullseye': FaBullseye,
  'FaRocket': FaRocket,
  'FaCode': FaCode,
  'FaFolder': FaFolder,
  'FaHome': FaHome,
  'FaTools': FaTools,
  'FaUser': FaUser,
  'FaEnvelope': FaEnvelope
};

// Icônes disponibles pour les projets
const projectIcons = [
  { name: 'FaShoppingCart', component: FaShoppingCart },
  { name: 'FaChartLine', component: FaChartLine },
  { name: 'FaComments', component: FaComments },
  { name: 'FaCheck', component: FaCheck },
  { name: 'FaBolt', component: FaBolt },
  { name: 'FaTwitter', component: FaTwitter },
  { name: 'FaLightbulb', component: FaLightbulb },
  { name: 'FaDatabase', component: FaDatabase },
  { name: 'FaBullseye', component: FaBullseye },
  { name: 'FaRocket', component: FaRocket },
  { name: 'FaCode', component: FaCode },
  { name: 'FaFolder', component: FaFolder },
  { name: 'FaHome', component: FaHome },
  { name: 'FaTools', component: FaTools },
  { name: 'FaUser', component: FaUser },
  { name: 'FaEnvelope', component: FaEnvelope }
];

function Admin() {
  const navigate = useNavigate();
  
  // Charger les préférences depuis localStorage SYNCHRONEMENT AVANT tout rendu
  const savedTheme = typeof window !== 'undefined' ? localStorage.getItem('theme') : null;
  const savedColor = typeof window !== 'undefined' ? localStorage.getItem('accentColor') : null;
  const initialDarkMode = savedTheme ? savedTheme === 'dark' : true;
  const initialAccentColor = savedColor || '#3b82f6';
  
  // APPLIQUER IMMÉDIATEMENT les variables CSS AVANT tout rendu React
  if (typeof window !== 'undefined') {
    applyThemeAndColor(initialDarkMode, initialAccentColor);
  }
  
  // État de chargement initial
  const [isLoading, setIsLoading] = useState(true);
  
  // Déconnexion : révoque la session côté serveur, pas seulement dans le navigateur.
  const handleLogout = async () => {
    await logoutSession();
    navigate('/login');
  };
  
  const [showForm, setShowForm] = useState(false);
  const [projects, setProjects] = useState([]);
  const [editingProject, setEditingProject] = useState(null);
  const [techSearchQuery, setTechSearchQuery] = useState(''); // État pour la recherche de technologies
  const [confirmState, setConfirmState] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: 'Confirmer',
    cancelLabel: 'Annuler',
    onConfirm: null,
    showCancel: true
  });
  
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    longDescription: '',
    link: '',
    contactLink: '',
    useGithub: false,
    githubLink: '',
    image: null,
    imagePreview: null, // Image principale pour le modal (obligatoire)
    cardDisplayType: 'icon', // 'icon' ou 'image' - pour l'affichage dans la carte
    cardImage: null,
    cardImagePreview: null, // Image optionnelle pour la carte
    selectedIcon: projectIcons[0],
    featured: false,
    technologies: {}
  });

  // Fonction pour charger les projets depuis l'API
  const loadProjects = React.useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${getApiUrl('/projects.php')}?t=${Date.now()}`, { cache: 'no-store' });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        throw new Error('Réponse serveur invalide (JSON attendu)');
      }
      
      if (data.success && Array.isArray(data.projects)) {
        setProjects(data.projects);
      } else {
        console.error('Format de réponse invalide:', data);
        setProjects([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des projets:', error);
      setProjects([]);
      // Ne pas utiliser localStorage, afficher un message d'erreur
      alert('Erreur: Impossible de charger les projets depuis le serveur. Vérifiez votre connexion.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Plus besoin d'initialiser le projet par défaut, la base de données gère ça

  // Vérifier l'authentification auprès du serveur, puis charger les données.
  useEffect(() => {
    let cancelled = false;

    const loadAdminData = async () => {
      const isAuthenticated = await verifySession();

      if (cancelled) {
        return;
      }

      if (!isAuthenticated) {
        navigate('/login');
        return;
      }

      loadProjects();
    };

    loadAdminData();

    return () => {
      cancelled = true;
    };
  }, [navigate, loadProjects]);

  const performDeleteProject = async (projectId) => {
    try {
      setIsLoading(true);
      const response = await apiFetch(`/projects.php?id=${projectId}`, { method: 'DELETE' });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : { success: true };
      } catch (e) {
        // Si le serveur ne renvoie pas de JSON mais que le statut est OK, on considère comme succès
        data = { success: true };
      }
      if (data.success) {
        await loadProjects();
      } else {
        throw new Error(data.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      if (error.name === 'UnauthorizedError') {
        navigate('/login');
        return;
      }
      setConfirmState({
        open: true,
        title: 'Erreur',
        message: error.message || 'Une erreur est survenue lors de la suppression du projet',
        confirmLabel: 'OK',
        cancelLabel: 'Annuler',
        onConfirm: null,
        showCancel: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteProject = (projectId) => {
    setConfirmState({
      open: true,
      title: 'Supprimer ce projet ?',
      message: 'Cette action est définitive. Voulez-vous vraiment supprimer ce projet ?',
      confirmLabel: 'Supprimer',
      cancelLabel: 'Annuler',
      onConfirm: () => performDeleteProject(projectId)
    });
  };

  const handleEditProject = (project) => {
    setEditingProject(project.id);
    setTechSearchQuery(''); // Réinitialiser la recherche
    // Mapper l'icône string vers l'objet
    const iconObj = projectIcons.find(i => i.name === project.icon) || projectIcons[0];
    const techsObj = {};
    project.technologies.forEach(tech => {
      techsObj[tech.name] = tech.level;
    });
    
    setFormData({
      title: project.title,
      description: project.description,
      longDescription: project.longDescription || project.description,
      link: project.link || '',
      contactLink: project.contactLink || '',
      useGithub: !!(project.githubLink),
      githubLink: project.githubLink || '',
      image: null,
      imagePreview: project.image || null,
      cardDisplayType: project.cardImage ? 'image' : 'icon',
      cardImage: null,
      cardImagePreview: project.cardImage || null,
      selectedIcon: iconObj,
      featured: project.featured === true || project.featured === 1 || project.featured === '1' || project.featured === 'true',
      technologies: techsObj
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingProject(null);
    setShowForm(false);
    setTechSearchQuery(''); // Réinitialiser la recherche
    setFormData({
      title: '',
      description: '',
      longDescription: '',
      link: '',
      contactLink: '',
      useGithub: false,
      githubLink: '',
      image: null,
      imagePreview: null,
      cardDisplayType: 'icon',
      cardImage: null,
      cardImagePreview: null,
      selectedIcon: projectIcons[0],
      featured: false,
      technologies: {}
    });
  };

  const handleNewProject = () => {
    setEditingProject(null);
    setTechSearchQuery(''); // Réinitialiser la recherche
    setFormData({
      title: '',
      description: '',
      longDescription: '',
      link: '',
      contactLink: '',
      useGithub: false,
      githubLink: '',
      image: null,
      imagePreview: null,
      cardDisplayType: 'icon',
      cardImage: null,
      cardImagePreview: null,
      selectedIcon: projectIcons[0],
      featured: false,
      technologies: {}
    });
    setShowForm(true);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e, type = 'main') => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'main') {
          setFormData(prev => ({
            ...prev,
            image: file,
            imagePreview: reader.result
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            cardImage: file,
            cardImagePreview: reader.result
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (type = 'main') => {
    if (type === 'main') {
      setFormData(prev => ({
        ...prev,
        image: null,
        imagePreview: null
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        cardImage: null,
        cardImagePreview: null
      }));
    }
  };

  const handleIconSelect = (icon) => {
    setFormData(prev => ({
      ...prev,
      selectedIcon: icon
    }));
  };

  const handleTechToggle = (techName) => {
    setFormData(prev => {
      const newTechs = { ...prev.technologies };
      if (newTechs[techName]) {
        delete newTechs[techName];
      } else {
        newTechs[techName] = availableTechs[techName].defaultLevel || 80;
      }
      return {
        ...prev,
        technologies: newTechs
      };
    });
  };

  const handleTechLevelChange = (techName, level) => {
    const value = Math.max(0, Math.min(100, parseInt(level, 10) || 0));
    setFormData(prev => ({
      ...prev,
      technologies: {
        ...prev.technologies,
        [techName]: value
      }
    }));
  };

  const performSubmit = async () => {
    try {
      setIsLoading(true);
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title.trim());
      formDataToSend.append('description', formData.description.trim());
      formDataToSend.append('longDescription', (formData.longDescription || formData.description).trim());
      formDataToSend.append('link', formData.link.trim() || '');
      formDataToSend.append('contactLink', '#');
      formDataToSend.append('githubLink', formData.useGithub && formData.githubLink.trim() ? formData.githubLink.trim() : '');
      formDataToSend.append('icon', formData.selectedIcon.name);
      const featuredValue = formData.featured === true || formData.featured === 'true' || formData.featured === 1 || formData.featured === '1';
      formDataToSend.append('featured', featuredValue ? '1' : '0');
      const technologies = Object.keys(formData.technologies).map(techName => ({
        name: techName,
        level: parseInt(formData.technologies[techName], 10) || availableTechs[techName]?.defaultLevel || 80
      }));
      formDataToSend.append('technologies', JSON.stringify(technologies));
      if (formData.image) formDataToSend.append('image', formData.image);
      if (formData.cardDisplayType === 'image' && formData.cardImage) {
        formDataToSend.append('cardImage', formData.cardImage);
      }
      // Si on édite et qu'on a retiré l'image carte (pas de preview et pas de nouveau fichier)
      if (editingProject && formData.cardDisplayType === 'image' && !formData.cardImage && !formData.cardImagePreview) {
        formDataToSend.append('removeCardImage', '1');
      }
      // Si on édite et qu'on choisit l'affichage "icône", on retire l'image de carte existante
      if (editingProject && formData.cardDisplayType === 'icon') {
        formDataToSend.append('removeCardImage', '1');
      }
      const endpoint = editingProject
        ? `/projects.php?id=${editingProject}`
        : '/projects.php';
      if (editingProject) {
        formDataToSend.append('_method', 'PUT');
      }
      const response = await apiFetch(endpoint, { method: 'POST', body: formDataToSend });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : { success: true };
      } catch (e) {
        // Si le serveur renvoie vide mais statut OK, on traite comme succès
        data = { success: true };
      }
      if (data.success) {
        await loadProjects();
        handleCancelEdit();
        setConfirmState({
          open: true,
          title: 'Succès',
          message: editingProject ? 'Le projet a été modifié avec succès.' : 'Le projet a été ajouté avec succès.',
          confirmLabel: 'OK',
          cancelLabel: 'Annuler',
          onConfirm: null,
          showCancel: false
        });
      } else {
        throw new Error(data.message || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      if (error.name === 'UnauthorizedError') {
        navigate('/login');
        return;
      }
      setConfirmState({
        open: true,
        title: 'Erreur',
        message: error.message || 'Une erreur est survenue lors de la sauvegarde du projet',
        confirmLabel: 'OK',
        cancelLabel: 'Annuler',
        onConfirm: null,
        showCancel: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title || !formData.title.trim()) {
      setConfirmState({
        open: true,
        title: 'Champs requis',
        message: 'Veuillez remplir le titre du projet',
        confirmLabel: 'OK',
        cancelLabel: 'Annuler',
        onConfirm: null,
        showCancel: false
      });
      return;
    }
    if (!formData.description || !formData.description.trim()) {
      setConfirmState({
        open: true,
        title: 'Champs requis',
        message: 'Veuillez remplir la description du projet',
        confirmLabel: 'OK',
        cancelLabel: 'Annuler',
        onConfirm: null,
        showCancel: false
      });
      return;
    }
    if (Object.keys(formData.technologies).length === 0) {
      setConfirmState({
        open: true,
        title: 'Sélection requise',
        message: 'Veuillez sélectionner au moins une technologie',
        confirmLabel: 'OK',
        cancelLabel: 'Annuler',
        onConfirm: null,
        showCancel: false
      });
      return;
    }
    setConfirmState({
      open: true,
      title: editingProject ? 'Confirmer la modification' : 'Confirmer l’ajout',
      message: editingProject ? 'Voulez-vous enregistrer les modifications de ce projet ?' : 'Voulez-vous ajouter ce nouveau projet ?',
      confirmLabel: editingProject ? 'Modifier' : 'Ajouter',
      cancelLabel: 'Annuler',
      onConfirm: performSubmit,
      showCancel: true
    });
  };

  const IconComponent = formData.selectedIcon.component;
  
  const stats = {
    total: projects.length,
    featured: projects.filter(p => p.featured).length,
    withImage: projects.filter(p => p.image).length
  };

  // Logs lourds supprimés pour alléger le rendu

  useEffect(() => {
    document.body.style.backgroundColor = 'var(--bg-primary)';
    document.body.style.color = 'var(--text-primary)';
    document.documentElement.style.backgroundColor = 'var(--bg-primary)';
    
    // Forcer le background de la page entière
    const appElement = document.querySelector('.app');
    if (appElement) {
      appElement.style.backgroundColor = 'var(--bg-primary)';
    }
  }, []);

  return (
    <div className="app admin-page" style={{ 
      background: 'var(--bg-primary)', 
      minHeight: '100vh', 
      position: 'relative', 
      zIndex: 10000, 
      color: 'var(--text-primary)',
      width: '100%',
      overflow: 'auto'
    }}>
      <section className="admin-section section-visible" style={{ 
        background: 'var(--bg-primary)', 
        padding: '5rem 2rem', 
        minHeight: '100vh', 
        position: 'relative', 
        zIndex: 10000 
      }}>
        <div className="admin-container" style={{ 
          maxWidth: '1000px', 
          margin: '0 auto', 
          position: 'relative', 
          zIndex: 10001, 
          color: 'var(--text-primary)',
          backgroundColor: 'transparent'
        }}>
          
          <div className="admin-dashboard-header" style={{ position: 'relative', zIndex: 10002 }}>
            <div className="admin-header-top">
              <button 
                className="btn btn-secondary admin-back-btn"
                onClick={() => navigate('/')}
              >
                <FaArrowLeft />
                <span>Retour</span>
              </button>
              <h1 className="admin-main-title" style={{ color: 'var(--text-primary)', fontSize: '2.5rem' }}>
                <FaTools />
                <span>Dashboard Administration</span>
              </h1>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <button 
                  className="btn btn-secondary admin-logout-btn"
                  onClick={handleLogout}
                  title="Déconnexion"
                >
                  <FaSignOutAlt />
                  <span>Déconnexion</span>
                </button>
                {!showForm && (
                  <button 
                    className="btn btn-primary admin-add-btn"
                    onClick={handleNewProject}
                  >
                    <FaPlus />
                    <span>Nouveau Projet</span>
                  </button>
                )}
              </div>
            </div>

            
            <div className="admin-stats-grid">
              <div className="admin-stat-card">
                <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                  <FaProjectDiagram />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{stats.total}</div>
                  <div className="stat-label">Projets totaux</div>
                </div>
              </div>
              <div className="admin-stat-card">
                <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                  <FaStar />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{stats.featured}</div>
                  <div className="stat-label">Projets phares</div>
                </div>
              </div>
              <div className="admin-stat-card">
                <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                  <FaImage />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{stats.withImage}</div>
                  <div className="stat-label">Avec image</div>
                </div>
              </div>
            </div>
          </div>

          
          {!showForm && projects.length > 0 && (
            <div className="admin-projects-section">
              <h2 className="admin-section-title">Mes Projets</h2>
              <div className="admin-projects-grid">
                {projects.map((project) => {
                  const ProjectIcon = iconMap[project.icon] || FaFolder;
                  return (
                    <div key={project.id} className={`admin-project-card ${project.featured ? 'featured' : ''}`}>
                      <div className="admin-project-card-header">
                        <div className="admin-project-icon">
                          <ProjectIcon />
                        </div>
                        {project.featured && (
                          <div className="admin-featured-badge">
                            <FaStar />
                            <span>Phare</span>
                          </div>
                        )}
                      </div>
                      <div className="admin-project-card-body">
                        <h3 className="admin-project-title">{project.title}</h3>
                        <p className="admin-project-description">{project.description}</p>
                        <div className="admin-project-techs">
                          {project.technologies && project.technologies.slice(0, 3).map((tech, idx) => (
                            <span key={idx} className="admin-tech-badge">{typeof tech === 'string' ? tech : tech.name}</span>
                          ))}
                          {project.technologies && project.technologies.length > 3 && (
                            <span className="admin-tech-badge">+{project.technologies.length - 3}</span>
                          )}
                        </div>
                      </div>
                      <div className="admin-project-card-actions">
                        <button 
                          className="admin-action-btn edit"
                          onClick={() => handleEditProject(project)}
                        >
                          <FaEdit />
                          <span>Modifier</span>
                        </button>
                        <button 
                          className="admin-action-btn delete"
                          onClick={() => handleDeleteProject(project.id)}
                        >
                          <FaTrash />
                          <span>Supprimer</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          
          {showForm && (
            <div className="admin-form-section">
              <div className="admin-form-header">
                <h2 className="admin-section-title">
                  {editingProject ? 'Modifier un projet' : 'Créer un nouveau projet'}
                </h2>
                <button 
                  className="admin-close-form-btn"
                  onClick={handleCancelEdit}
                >
                  <FaTimes />
                </button>
              </div>

          
          <form className="admin-form" onSubmit={handleSubmit}>
            
            <div className="form-group">
              <label className="form-label">Titre du projet *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Ex: E-Commerce Platform"
                required
              />
            </div>

            
            <div className="form-group">
              <label className="form-label">Description courte *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="form-textarea"
                placeholder="Une description courte du projet"
                rows="3"
                required
              />
            </div>

            
            <div className="form-group">
              <label className="form-label">Description détaillée</label>
              <textarea
                name="longDescription"
                value={formData.longDescription}
                onChange={handleInputChange}
                className="form-textarea"
                placeholder="Une description plus détaillée du projet (optionnel)"
                rows="5"
              />
            </div>

            
            <div className="form-group">
              <label className="form-label">Image principale pour le modal *</label>
              {formData.imagePreview ? (
                <div className="image-preview-container">
                  <img 
                    src={formData.imagePreview} 
                    alt="Preview" 
                    className="image-preview"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage('main')}
                    className="image-remove-btn"
                  >
                    <FaTimes />
                  </button>
                </div>
              ) : (
                <div className="image-upload-area">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, 'main')}
                    id="image-upload-main"
                    className="image-upload-input"
                  />
                  <label htmlFor="image-upload-main" className="image-upload-label">
                    <FaImage />
                    <span>Cliquez pour importer l'image principale (obligatoire)</span>
                  </label>
                </div>
              )}
            </div>

            
            <div className="form-group">
              <label className="form-label">Affichage dans la carte</label>
              <div className="form-radio-group">
                <label className="form-radio-label">
                  <input
                    type="radio"
                    name="cardDisplayType"
                    value="icon"
                    checked={formData.cardDisplayType === 'icon'}
                    onChange={(e) => setFormData(prev => ({ ...prev, cardDisplayType: 'icon' }))}
                    className="form-radio"
                  />
                  <span>Utiliser une icône</span>
                </label>
                <label className="form-radio-label">
                  <input
                    type="radio"
                    name="cardDisplayType"
                    value="image"
                    checked={formData.cardDisplayType === 'image'}
                    onChange={(e) => setFormData(prev => ({ ...prev, cardDisplayType: 'image' }))}
                    className="form-radio"
                  />
                  <span>Utiliser une image</span>
                </label>
              </div>
            </div>

            
            {formData.cardDisplayType === 'icon' && (
              <div className="form-group">
                <label className="form-label">Icône du projet</label>
                <div className="icon-selector">
                  {projectIcons.map((icon, index) => {
                    const IconComp = icon.component;
                    return (
                      <button
                        key={index}
                        type="button"
                        className={`icon-option ${formData.selectedIcon.name === icon.name ? 'active' : ''}`}
                        onClick={() => handleIconSelect(icon)}
                      >
                        <IconComp />
                      </button>
                    );
                  })}
                </div>
                <div className="selected-icon-preview">
                  <span>Icône sélectionnée :</span>
                  <IconComponent />
                </div>
              </div>
            )}

            
            {formData.cardDisplayType === 'image' && (
              <div className="form-group">
                <label className="form-label">Image pour la carte (optionnel)</label>
                {formData.cardImagePreview ? (
                  <div className="image-preview-container">
                    <img 
                      src={formData.cardImagePreview} 
                      alt="Preview carte" 
                      className="image-preview"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage('card')}
                      className="image-remove-btn"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <div className="image-upload-area">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, 'card')}
                      id="image-upload-card"
                      className="image-upload-input"
                    />
                    <label htmlFor="image-upload-card" className="image-upload-label">
                      <FaImage />
                      <span>Cliquez pour importer une image pour la carte</span>
                    </label>
                  </div>
                )}
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                  Si aucune image n'est fournie, l'icône sera utilisée par défaut
                </p>
              </div>
            )}

            
            <div className="form-group">
              <label className="form-checkbox-label">
                <input
                  type="checkbox"
                  name="useGithub"
                  checked={formData.useGithub}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
                <span>Disponible sur GitHub</span>
              </label>
              {formData.useGithub && (
                <input
                  type="url"
                  name="githubLink"
                  value={formData.githubLink}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="https://github.com/username/repo"
                  style={{ marginTop: '0.75rem' }}
                />
              )}
            </div>

            
            <div className="form-group">
              <label className="form-label">Lien vers le projet</label>
              <input
                type="url"
                name="link"
                value={formData.link}
                onChange={handleInputChange}
                className="form-input"
                placeholder="https://mon-projet.com"
              />
            </div>

            
            <div className="form-group">
              <label className="form-label">Technologies utilisées *</label>
              
              
              <div className="tech-search-container">
                <div className="tech-search-input-wrapper">
                  <FaSearch className="tech-search-icon" />
                  <input
                    type="text"
                    className="tech-search-input"
                    placeholder="Rechercher une technologie (ex: HTML, React, Python...)"
                    value={techSearchQuery}
                    onChange={(e) => setTechSearchQuery(e.target.value)}
                  />
                  {techSearchQuery && (
                    <button
                      type="button"
                      className="tech-search-clear"
                      onClick={() => setTechSearchQuery('')}
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              </div>

              <div className="techs-container">
                {Object.keys(availableTechs)
                  .filter((techName) => {
                    if (!techSearchQuery) return true;
                    return techName.toLowerCase().includes(techSearchQuery.toLowerCase());
                  })
                  .map((techName) => {
                    const tech = availableTechs[techName];
                    if (!tech || !tech.icon) {
                      return null;
                    }
                    const TechIcon = tech.icon;
                    const isSelected = formData.technologies.hasOwnProperty(techName);
                    
                    return (
                      <div key={techName} className="tech-item">
                        <div className="tech-header">
                          <label className="tech-checkbox-label">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleTechToggle(techName)}
                              className="tech-checkbox"
                            />
                            <TechIcon style={{ color: tech.color, fontSize: '1.5rem' }} />
                            <span className="tech-name">{techName}</span>
                          </label>
                        </div>
                        {isSelected && (() => {
                          const currentLevel = formData.technologies[techName] || tech.defaultLevel;
                          return (
                            <div className="tech-level-input" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.35rem' }}>
                              <input
                                type="range"
                                min="0"
                                max="100"
                                value={currentLevel}
                                onChange={(e) => handleTechLevelChange(techName, e.target.value)}
                                className="tech-level-slider"
                                style={{
                                  background: `linear-gradient(90deg, var(--accent-gradient) 0%, var(--accent-gradient) ${currentLevel}%, rgba(255,255,255,0.08) ${currentLevel}%)`
                                }}
                              />
                              <span className="tech-level-badge">
                                {currentLevel}%
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
              </div>
              {techSearchQuery && Object.keys(availableTechs).filter((techName) => 
                techName.toLowerCase().includes(techSearchQuery.toLowerCase())
              ).length === 0 && (
                <p className="tech-search-no-results">
                  Aucune technologie trouvée pour "{techSearchQuery}"
                </p>
              )}
            </div>

            
            <div className="form-group">
              <label className="form-checkbox-label">
                <input
                  type="checkbox"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
                <span>Marquer comme projet phare</span>
                <FaRocket style={{ marginLeft: '0.5rem', color: 'var(--accent-color)' }} />
              </label>
            </div>

            
            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-secondary admin-cancel-btn"
                onClick={handleCancelEdit}
              >
                <FaTimes />
                <span>Annuler</span>
              </button>
              <button type="submit" className="btn btn-primary admin-submit-btn">
                <FaSave />
                <span>{editingProject ? 'Modifier' : 'Ajouter'} le projet</span>
              </button>
            </div>
          </form>
          </div>
          )}

          
          {projects.length === 0 && !showForm && (
            <div className="admin-empty-state">
              <div className="empty-state-icon">
                <FaProjectDiagram />
              </div>
              <h3>Aucun projet pour le moment</h3>
              <p>Commencez par ajouter votre premier projet !</p>
              <button 
                className="btn btn-primary"
                onClick={handleNewProject}
              >
                <FaPlus />
                <span>Créer mon premier projet</span>
              </button>
            </div>
          )}
        </div>
      </section>

      {confirmState.open && (
        <div
          className="confirm-modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100000
          }}
          onClick={() => setConfirmState(prev => ({ ...prev, open: false }))}
        >
          <div
            className="confirm-modal"
            style={{
              background: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              borderRadius: '16px',
              padding: '1.5rem',
              width: 'min(92vw, 420px)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: 0, fontSize: '1.25rem' }}>{confirmState.title}</h3>
            <p style={{ marginTop: '0.75rem', color: 'var(--text-secondary)' }}>{confirmState.message}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.25rem' }}>
              {confirmState.showCancel && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setConfirmState(prev => ({ ...prev, open: false }))}
                >
                  {confirmState.cancelLabel || 'Annuler'}
                </button>
              )}
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  const fn = confirmState.onConfirm;
                  setConfirmState(prev => ({ ...prev, open: false }));
                  if (typeof fn === 'function') fn();
                }}
              >
                {confirmState.confirmLabel || 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;
